import { ShieldAlert } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Role permission helpers. The backend is the source of truth — these
 * helpers keep the UI consistent so restricted users never see actions
 * they cannot execute (defence in depth).
 */
export const ROLE_LEVEL = {
  staff: 1, waiter: 1, kitchen: 1,
  accountant: 2,
  manager: 3, restaurant_manager: 3,
  owner: 4, restaurant_owner: 4,
  brand_manager: 5,
  regional_manager: 5,
  organization_admin: 6,
  organization_owner: 7,
};

export const PERSONNEL_MANAGERS = new Set([
  "owner", "restaurant_owner",
  "manager", "restaurant_manager",
  "organization_owner", "organization_admin",
  "brand_manager", "regional_manager",
]);

export const COPILOT_READERS = new Set([
  "owner", "restaurant_owner",
  "manager", "restaurant_manager",
  "accountant",
  "organization_owner", "organization_admin",
  "brand_manager", "regional_manager",
]);

export const FINANCE_VIEWERS = new Set([
  "owner", "restaurant_owner",
  "manager", "restaurant_manager",
  "accountant",
  "organization_owner", "organization_admin",
  "brand_manager", "regional_manager",
]);

export const HQ_ROLES = new Set([
  "owner", "restaurant_owner",
  "organization_owner", "organization_admin",
  "brand_manager", "regional_manager",
]);

export const STAFF_TIER = new Set(["staff", "waiter", "kitchen"]);

export function roleLevel(role) {
  return ROLE_LEVEL[(role || "").toLowerCase()] || 0;
}

export function canManagePersonnel(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return PERSONNEL_MANAGERS.has(user.role);
}

export function canUseCopilot(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return COPILOT_READERS.has(user.role);
}

export function canViewFinance(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return FINANCE_VIEWERS.has(user.role);
}

export function canAccessHQ(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return HQ_ROLES.has(user.role);
}

export function isStaffTier(user) {
  return STAFF_TIER.has((user?.role || "").toLowerCase());
}

export function assignableRoles(actorRole) {
  const my = roleLevel(actorRole);
  const list = [
    "staff", "kitchen", "waiter", "accountant",
    "manager", "restaurant_manager",
    "brand_manager", "regional_manager",
    "organization_admin",
  ];
  return list.filter(r => roleLevel(r) < my);
}

/** Reusable "Access denied" panel used on any restricted page. */
export function AccessDenied({ testid = "access-denied" }) {
  const { lang } = useI18n();
  const de = lang === "de";
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid={testid}>
      <div className="max-w-md text-center space-y-3">
        <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
          <ShieldAlert className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-semibold">
          {de ? "Zugriff verweigert" : "Yetkiniz yok"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {de
            ? "Deine Rolle hat keinen Zugriff auf diesen Bereich. Bitte wende dich an den Eigentümer oder Administrator."
            : "Rolünüz bu bölüme erişim izni vermiyor. Lütfen sahibi veya yöneticiyle iletişime geç."}
        </p>
      </div>
    </div>
  );
}
